/**
 * Centralized Navigation Configuration
 *
 * This file defines all sections and their tabs for the application navigation.
 * It serves as the single source of truth for routing, feature flags, and navigation UI.
 */

import type { CapabilityKey } from '@bett3r-dev/teselly-capabilities';

export interface TabConfig {
  id: string;
  labelKey: string;
  translationNamespace: string;
  defaultLabel: string;
  flagKey: string;
  subtitleKey?: string; // Optional translation key for subtitle
  defaultSubtitle?: string; // Optional default subtitle text
  aiHidden?: boolean; // excluded from AI route taxonomy when true
  capabilityKey?: CapabilityKey; // If set, tab is hidden unless user has this capability
}

export interface SectionConfig {
  id: string;
  labelKey: string;
  translationNamespace: string;
  defaultLabel: string;
  flagKey: string;
  path: string;
  tabs?: TabConfig[];
  showInNavbar?: boolean; // Defaults to true if not specified
  aiHidden?: boolean; // excluded from AI route taxonomy when true
  capabilityKey?: CapabilityKey; // If set, section is hidden unless user has this capability
}

/**
 * Complete navigation configuration for all application sections
 */
export const NAVIGATION_CONFIG: SectionConfig[] = [
  // Sections without tabs
  {
    id: 'dashboard',
    labelKey: 'dashboard_label',
    translationNamespace: 'layout',
    defaultLabel: 'Dashboard',
    flagKey: 'dashboard',
    path: '/dashboard'
  },
  {
    id: 'reports',
    labelKey: 'reports_label',
    translationNamespace: 'layout',
    defaultLabel: 'Reports',
    flagKey: 'reports',
    path: '/reports',
    capabilityKey: 'analytics:layouts:read'
  },

  // Sections with tabs
  {
    id: 'publications',
    labelKey: 'publications_label',
    translationNamespace: 'layout',
    defaultLabel: 'Publications',
    flagKey: 'publications',
    path: '/publications',
    capabilityKey: 'items-publishing:publications:read',
    tabs: [
      {
        id: 'list',
        labelKey: 'publications_list_tab',
        translationNamespace: 'publications',
        defaultLabel: 'Publications List',
        flagKey: 'publications-list',
        capabilityKey: 'items-publishing:publications:read'
      },
      {
        id: 'create',
        labelKey: 'publications_create_tab',
        translationNamespace: 'publications',
        defaultLabel: 'Create Publication',
        flagKey: 'publications-create',
        capabilityKey: 'items-publishing:publications:create'
      },
      {
        id: 'spreadsheet',
        labelKey: 'bulk_edit_tab',
        translationNamespace: 'publications',
        defaultLabel: 'Bulk Edit',
        flagKey: 'publications-bulkEdit',
        capabilityKey: 'items-publishing:publications:update',
        subtitleKey: 'bulk_edit_publications_subtitle',
        defaultSubtitle: 'Edit multiple publications using a spreadsheet interface'
      }
    ]
  },
  {
    id: 'inventory',
    labelKey: 'inventory_label',
    translationNamespace: 'layout',
    defaultLabel: 'Inventory',
    flagKey: 'inventory',
    path: '/inventory',
    capabilityKey: 'items-publishing:items:read',
    tabs: [
      {
        id: 'list',
        labelKey: 'item_list_tab',
        translationNamespace: 'inventory',
        defaultLabel: 'Item List',
        flagKey: 'inventory-list',
        capabilityKey: 'items-publishing:items:read'
      },
      {
        id: 'createItem',
        labelKey: 'create_item_tab',
        translationNamespace: 'inventory',
        defaultLabel: 'Create Item',
        flagKey: 'inventory-create',
        capabilityKey: 'items-publishing:items:create'
      },
      {
        id: 'import',
        labelKey: 'import_tab',
        translationNamespace: 'inventory',
        defaultLabel: 'Import',
        flagKey: 'inventory-import',
        capabilityKey: 'items-publishing:items:create'
      },
      {
        id: 'stockList',
        labelKey: 'stock_list_tab',
        translationNamespace: 'inventory',
        defaultLabel: 'Stock',
        flagKey: 'inventory-stockLists',
        capabilityKey: 'stock-management:stock:read'
      },
      {
        id: 'priceList',
        labelKey: 'price_list_tab',
        translationNamespace: 'inventory',
        defaultLabel: 'Price List',
        flagKey: 'inventory-priceLists',
        capabilityKey: 'items-publishing:price-lists:read'
      },
      {
        // Size-guide library — lives in Inventory alongside the other
        // spreadsheet-backed views (it is a publications-facing tool, but
        // managed here). Route: /inventory/sizeGuides.
        id: 'sizeGuides',
        labelKey: 'size_guides_tab',
        translationNamespace: 'inventory',
        defaultLabel: 'Size Guides',
        flagKey: 'inventory-sizeGuides',
        capabilityKey: 'items-publishing:size-guides:manage',
        // Excluded from the AI route taxonomy until the matching entry lands in
        // `@bett3r-dev/teselly-navigation-taxonomy` (outside this PR's scope).
        // `aiHidden` keeps the navigation.config drift-guard test green; the tab
        // still renders. (Same workaround the standalone section used.)
        aiHidden: true
      },
      {
        id: 'spreadsheet',
        labelKey: 'bulk_edit_tab',
        translationNamespace: 'inventory',
        defaultLabel: 'Bulk Edit',
        flagKey: 'inventory-bulkEdit',
        capabilityKey: 'items-publishing:items:bulk',
        subtitleKey: 'bulk_edit_inventory_subtitle',
        defaultSubtitle: 'Edit multiple items using a spreadsheet interface'
      }
    ]
  },
  {
    id: 'integrations',
    labelKey: 'integrations_label',
    translationNamespace: 'layout',
    defaultLabel: 'Integrations',
    flagKey: 'integrations',
    path: '/integrations',
    capabilityKey: 'sales-channels:read',
    tabs: [
      {
        id: 'channels',
        labelKey: 'channels_tab',
        translationNamespace: 'integrations',
        defaultLabel: 'Channels',
        flagKey: 'integrations-channels',
        capabilityKey: 'sales-channels:read'
      },
      {
        id: 'erps',
        labelKey: 'erps_tab',
        translationNamespace: 'integrations',
        defaultLabel: 'ERPs',
        flagKey: 'integrations-erps',
        capabilityKey: 'sales-channels:read'
      },
      {
        id: 'invoicing',
        labelKey: 'invoicing_tab',
        translationNamespace: 'integrations',
        defaultLabel: 'Invoicing',
        flagKey: 'integrations-invoicing',
        capabilityKey: 'sales-channels:read'
      },
      {
        id: 'logistics',
        labelKey: 'logistics_tab',
        translationNamespace: 'integrations',
        defaultLabel: 'Logistics',
        flagKey: 'integrations-logistics',
        capabilityKey: 'sales-channels:read'
      },
      {
        id: 'messaging',
        labelKey: 'messaging_tab',
        translationNamespace: 'integrations',
        defaultLabel: 'Communications',
        flagKey: 'integrations-messaging',
        capabilityKey: 'sales-channels:read'
      }
    ]
  },
  {
    id: 'billing',
    labelKey: 'billing_label',
    translationNamespace: 'layout',
    defaultLabel: 'Billing',
    flagKey: 'billing',
    path: '/billing',
    capabilityKey: 'clients-management:subscription:read'
  },
  /**
   * TV1-2071 / S5 — the tenant's OWN subscription (what I owe, my invoices, my
   * card, and the pay-to-resume action). Distinct from the `billing` section above,
   * which is the ADMIN-side product catalogue.
   *
   * ⚠️ `aiHidden: true`, for two reasons that agree (DEC-5):
   *  · `config/__tests__/navigation.config.test.ts` is a BIDIRECTIONAL drift guard
   *    against `NAVIGATION_TAXONOMY` in `@bett3r-dev/teselly-navigation-taxonomy`. A
   *    non-`aiHidden` section with no matching taxonomy entry fails it, and adding
   *    the entry means a cross-package change plus dodging `billing`'s
   *    `aiPhrasings` uniqueness — for a surface the AI is barred from anyway.
   *  · It is semantically right. `ROUTE_REGISTRY` already declares `/subscription`
   *    as `allowedSources: ['user','url'] // ❌ AI cannot access billing`, and the
   *    domain's `canApply` denies the `ai` source every one of its commands. This is
   *    the third of those three gates.
   *
   * The route itself already exists (`routes/index.tsx` +
   * `domains/navigation/routeRegistry.ts`, flag `subscription`, capability
   * `clients-management:subscription:read`, `homeRedirectFallback: true` — which is
   * what lands a suspended tenant here). Only this nav entry was missing, so the
   * screen had no discoverable way in.
   *
   * The tab ids and their flags mirror `SubscriptionScreen`'s own tab list. `usage`
   * is declared but its `subscription-usage` flag is now `false` in all four seeds
   * (DEC-6): `UsageMetrics` is `Math.random()`.
   */
  {
    id: 'subscription',
    labelKey: 'subscription_page_title',
    translationNamespace: 'subscription',
    defaultLabel: 'Subscription',
    flagKey: 'subscription',
    path: '/subscription',
    showInNavbar: false, // Reached from the account menu and the suspension banner.
    aiHidden: true,
    capabilityKey: 'clients-management:subscription:read',
    tabs: [
      {
        id: 'plan',
        labelKey: 'subscription_tab_plan',
        translationNamespace: 'subscription',
        defaultLabel: 'Plan Overview',
        flagKey: 'subscription-plan',
        aiHidden: true
      },
      {
        id: 'usage',
        labelKey: 'subscription_tab_usage',
        translationNamespace: 'subscription',
        defaultLabel: 'Usage',
        flagKey: 'subscription-usage',
        aiHidden: true
      },
      {
        id: 'payment',
        labelKey: 'subscription_tab_payment',
        translationNamespace: 'subscription',
        defaultLabel: 'Payment Method',
        flagKey: 'subscription-payment',
        aiHidden: true
      },
      {
        id: 'invoices',
        labelKey: 'subscription_tab_invoices',
        translationNamespace: 'subscription',
        defaultLabel: 'Invoices',
        flagKey: 'subscription-invoices',
        aiHidden: true
      }
    ]
  },
  {
    id: 'orders',
    labelKey: 'orders_label',
    translationNamespace: 'layout',
    defaultLabel: 'Orders',
    flagKey: 'orders',
    path: '/orders',
    capabilityKey: 'sales:orders:read',
    tabs: [
      {
        id: 'list',
        labelKey: 'orders_list_tab',
        translationNamespace: 'orders',
        defaultLabel: 'Orders List',
        flagKey: 'orders-list',
        capabilityKey: 'sales:orders:read'
      },
      {
        id: 'create',
        labelKey: 'order_create_tab',
        translationNamespace: 'orders',
        defaultLabel: 'Create Order',
        flagKey: 'orders-create',
        capabilityKey: 'sales:orders:place'
      }
    ]
  },
  {
    id: 'warehouses',
    labelKey: 'warehouses_label',
    translationNamespace: 'layout',
    defaultLabel: 'Warehouses',
    flagKey: 'warehouses',
    path: '/warehouses',
    capabilityKey: 'stock-management:warehouses:read'
  },
  // TV1-2158 (S7) — the procurement suppliers master. Gated on the READ
  // capability: an OPERATOR (read only) must see the section, because a
  // deactivated supplier stays visible for the historical documents that
  // reference it. The manage-only affordances inside carry their own gates.
  {
    id: 'suppliers',
    labelKey: 'suppliers_label',
    translationNamespace: 'layout',
    defaultLabel: 'Suppliers',
    flagKey: 'suppliers',
    path: '/suppliers',
    capabilityKey: 'procurement:suppliers:read'
  },
  {
    id: 'interactions',
    labelKey: 'interactions_label',
    translationNamespace: 'layout',
    defaultLabel: 'Interactions',
    flagKey: 'interactions',
    path: '/interactions',
    capabilityKey: 'sales:messaging:questions:read',
    tabs: [
      {
        id: 'questions',
        labelKey: 'questions_tab',
        translationNamespace: 'interactions',
        defaultLabel: 'Q&A',
        flagKey: 'interactions-questions',
        // Explicit ( was implicit via the section key ): the section gate is now
        // any-of over the tab keys, so an implicitly-gated tab would be reachable
        // with only a sibling tab's grant. See `sectionGateCapabilities`.
        capabilityKey: 'sales:messaging:questions:read'
      },
      {
        // TV1-2259 slice 5 — the post-sale buyer-messages inbox
        // (`sales_channel_messages_readmodel`, slice 4). Read-only for now.
        id: 'messages',
        labelKey: 'messages_tab',
        translationNamespace: 'interactions',
        defaultLabel: 'Mensajes posventa',
        flagKey: 'interactions-messages',
        capabilityKey: 'sales:messaging:messages:read'
      }
    ]
  },
  {
    // TV1-2209 — the comprobantes screen. A single-view section: no tabs, so no
    // `tabs` array and no tab flags.
    id: 'invoices',
    labelKey: 'invoices_label',
    translationNamespace: 'layout',
    defaultLabel: 'Invoices',
    flagKey: 'invoices',
    path: '/invoices',
    capabilityKey: 'invoicing:fiscal-documents:read'
  },
  {
    id: 'accounting',
    labelKey: 'accounting_label',
    translationNamespace: 'layout',
    defaultLabel: 'Accounting',
    flagKey: 'accounting',
    path: '/accounting',
    capabilityKey: 'accounting:read',
    tabs: [
      {
        id: 'chart',
        labelKey: 'tabs.chart',
        translationNamespace: 'accounting',
        defaultLabel: 'Chart of Accounts',
        flagKey: 'accounting-chart',
        capabilityKey: 'accounting:read'
      },
      {
        id: 'journal',
        labelKey: 'tabs.journal',
        translationNamespace: 'accounting',
        defaultLabel: 'Journal Entries',
        flagKey: 'accounting-journal',
        capabilityKey: 'accounting:read'
      },
      {
        id: 'periods',
        labelKey: 'tabs.periods',
        translationNamespace: 'accounting',
        defaultLabel: 'Periods',
        flagKey: 'accounting-periods',
        capabilityKey: 'accounting:read'
      },
      {
        id: 'trial-balance',
        labelKey: 'tabs.trial-balance',
        translationNamespace: 'accounting',
        defaultLabel: 'Trial Balance',
        flagKey: 'accounting-trial-balance',
        capabilityKey: 'accounting:read'
      },
      {
        id: 'ledger',
        labelKey: 'tabs.ledger',
        translationNamespace: 'accounting',
        defaultLabel: 'Ledger',
        flagKey: 'accounting-ledger',
        capabilityKey: 'accounting:read'
      }
    ]
  },
  {
    id: 'accountSettings',
    labelKey: 'account_settings_label',
    translationNamespace: 'account',
    defaultLabel: 'Account Settings',
    flagKey: 'account',
    path: '/accountSettings',
    showInNavbar: false, // Accessed via AccountMenu dropdown instead
    tabs: [
      {
        id: 'accountInfo',
        labelKey: 'account_settings_account_info_tab',
        translationNamespace: 'account',
        defaultLabel: 'Account Info',
        flagKey: 'account'
      },
      {
        id: 'rules',
        labelKey: 'account_settings_rules_tab',
        translationNamespace: 'account',
        defaultLabel: 'Rules',
        flagKey: 'account-rules'
      },
      {
        id: 'billing',
        labelKey: 'account_settings_billing_tab',
        translationNamespace: 'account',
        defaultLabel: 'Billing',
        flagKey: 'account-billing'
      },
      {
        id: 'users',
        labelKey: 'account_settings_users_tab',
        translationNamespace: 'account',
        defaultLabel: 'Users',
        flagKey: 'account-users'
      },
      {
        id: 'roles',
        labelKey: 'account_settings_roles_tab',
        translationNamespace: 'account',
        defaultLabel: 'Roles',
        flagKey: 'account',
        capabilityKey: 'identity:roles:update'
      },
      {
        id: 'itemsPublishing',
        labelKey: 'account_settings_items_publishing_tab',
        translationNamespace: 'account',
        defaultLabel: 'Items Publishing',
        flagKey: 'account'
      },
      {
        id: 'orderStatuses',
        labelKey: 'account_settings_order_statuses_tab',
        translationNamespace: 'account',
        defaultLabel: 'Order Statuses',
        flagKey: 'account-orderStatuses'
      }
    ]
  },
  {
    id: 'userSettings',
    labelKey: 'user_settings_label',
    translationNamespace: 'userSettings',
    defaultLabel: 'User Settings',
    flagKey: 'account',
    path: '/userSettings',
    showInNavbar: false,
    tabs: [
      {
        id: 'profile',
        labelKey: 'user_settings_profile_tab',
        translationNamespace: 'userSettings',
        defaultLabel: 'Profile',
        flagKey: 'account'
      },
      {
        id: 'security',
        labelKey: 'user_settings_security_tab',
        translationNamespace: 'userSettings',
        defaultLabel: 'Security',
        flagKey: 'account'
      }
    ]
  }
];

/**
 * Helper function to get section configuration by ID
 */
export const getSectionConfig = ( sectionId: string ): SectionConfig | undefined => {
  return NAVIGATION_CONFIG.find( section => section.id === sectionId );
};

/**
 * Helper function to get all sections with tabs
 */
export const getSectionsWithTabs = (): SectionConfig[] => {
  return NAVIGATION_CONFIG.filter( section => section.tabs && section.tabs.length > 0 );
};

/**
 * Helper function to get all sections without tabs
 */
export const getSectionsWithoutTabs = (): SectionConfig[] => {
  return NAVIGATION_CONFIG.filter( section => !section.tabs || section.tabs.length === 0 );
};

/**
 * Helper function to check if a section has tabs
 */
export const sectionHasTabs = ( sectionId: string ): boolean => {
  const section = getSectionConfig( sectionId );
  return Boolean( section?.tabs && section.tabs.length > 0 );
};

/**
 * The set of capabilities that admit a caller into a section — ANY-OF.
 *
 * A section is entered through its sidebar entry ( `UserNavbar` ) and its route
 * guard ( `GatedRoute` in `routes/index.tsx` ). Both MUST admit every capability
 * that one of the section's own tabs is gated on, otherwise a grant of that tab
 * capability is inert: the sidebar entry stays hidden and a direct navigation
 * lands on `/403` before `useTabNavigation`'s per-tab filter ever runs.
 * ( TV1-2259 slice 5: `sales:messaging:messages:read` was inert this way while
 * both gates keyed on `sales:messaging:questions:read`. )
 *
 * Semantics: section `capabilityKey` ∪ every tab `capabilityKey`. The section
 * stays gated overall — a caller holding none of these keys is still refused —
 * and the per-tab filter still restricts WHICH tabs render. Pinned by
 * `__tests__/navigation.capabilityGates.test.ts`.
 *
 * A tab that declares NO `capabilityKey` is implicitly gated by the section key
 * alone; with any-of above it, such a tab would become reachable via a sibling
 * tab's grant. Every tab in a capability-gated section that needs its own gate
 * must therefore declare it explicitly ( see the `interactions` tabs ).
 *
 * ⚠️ LATENT TRAP, inert today — a section with NO `capabilityKey` but at least one
 * KEYED tab gets a non-empty set here, which converts an UNGATED section into a
 * GATED one. Exactly one section matches: `accountSettings` ( no section key, its
 * `roles` tab keyed `identity:roles:update` ), which would put `accountInfo`,
 * `rules`, `billing`, `users` and `itemsPublishing` behind a role-admin capability.
 * It cannot bite right now, on any of the three consumers:
 *   - the navbar filter short-circuits on `showInNavbar !== false` before `canAny`;
 *   - there is no `SECTION_GATE.accountSettings` route site ( only `FlaggedRoute` );
 *   - `/accountSettings` is `category: 'sensitive'`, so it is outside
 *     `HOME_REDIRECT_ORDER` and has no `homeRedirectFallback`.
 * Flipping `showInNavbar` to true, or wrapping that section in a `GatedRoute`,
 * would silently gate account settings on role-admin. Give the section its own
 * `capabilityKey` before doing either.
 */
export const sectionGateCapabilities = ( section: SectionConfig ): CapabilityKey[] => {
  const keys = new Set<CapabilityKey>();
  if ( section.capabilityKey ) keys.add( section.capabilityKey );
  for ( const tab of section.tabs ?? []) {
    if ( tab.capabilityKey ) keys.add( tab.capabilityKey );
  }
  return [...keys];
};
